import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { APP_ROUTE_PATHS, BASE_PATHS } from '@oneclick/constants/navigation';
import { AppLayout } from '@oneclick/layout/AppLayout';
import { ProtectedRoute } from 'components/router';
import { GlobalSearchProvider } from '@oneclick/features/global-search';

import { ClusterController } from '@oneclick/pages/cluster/ClusterController';
import { ClusterGate } from '@oneclick/pages/clusters/ClusterGate';
import { DbOptimizationController } from '@oneclick/pages/dbo/DbOptimizationController';
import {
  getClustersHomePage,
  getOrgShellPage,
} from '@oneclick/pages/orgRouteConfig';
import { AccessControlController } from '@oneclick/pages/access-control/AccessControlController';
import { BudgetsController } from '@oneclick/pages/budgets/BudgetsController';
import { CustomDashboardsController } from '@oneclick/pages/custom-dashboards/CustomDashboardsController';
import { OnboardingPage } from '@oneclick/pages/onboarding/OnboardingPage';
import { OverviewController } from '@oneclick/pages/overview/OverviewController';
import { SettingsController } from '@oneclick/pages/settings/SettingsController';
import { SsoController } from '@oneclick/pages/settings/SsoController';
import { ForbiddenPage } from '@oneclick/pages/error/ForbiddenPage';

const ClustersHomePage = getClustersHomePage();
const CloudVmsPage = getOrgShellPage('cloudVms');
const ApaPage = getOrgShellPage('apa');
const EventLogPage = getOrgShellPage('eventLog');
const NotificationsPage = getOrgShellPage('notifications');
const NetworkIntelligencePage = getOrgShellPage('networkIntelligence');
const EnterprisePage = getOrgShellPage('enterprise');
const ApiAccessPage = getOrgShellPage('apiAccess');
const CostReportPage = getOrgShellPage('costReport');

/** Rewrite pathname prefix and preserve search/hash. */
const PathReplaceRedirect = ({ from, to }: { from: RegExp; to: string }) => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate to={`${pathname.replace(from, to)}${search}${hash}`} replace />
  );
};

// Split-me components generate non-OP cluster URLs (e.g. /external-clusters/:id/...).
// Redirect them to the canonical oneclick paths under /automation/.
const PrependAutomationRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`/automation${pathname}${search}${hash}`} replace />;
};

export const App = () => (
  <GlobalSearchProvider>
    <Routes>
      <Route index element={<ClusterGate />} />
      <Route
        path={APP_ROUTE_PATHS.forbidden}
        element={
          <AppLayout noSidebar>
            <ForbiddenPage />
          </AppLayout>
        }
      />

      <Route element={<AppLayout />}>
        <Route index element={<Navigate to={BASE_PATHS.clusters} replace />} />
        <Route path={APP_ROUTE_PATHS.clusters} element={<ClustersHomePage />} />
        <Route
          path={APP_ROUTE_PATHS.externalClusters}
          element={<ClusterController />}
        />
        <Route path={APP_ROUTE_PATHS.costReport} element={<CostReportPage />} />
        <Route
          path={APP_ROUTE_PATHS.overview}
          element={<OverviewController />}
        />
        <Route element={<ProtectedRoute subject="OrganizationDbOptimizer" />}>
          <Route
            path={APP_ROUTE_PATHS.dbo}
            element={<DbOptimizationController />}
          />
        </Route>
        <Route path={APP_ROUTE_PATHS.cloudVms} element={<CloudVmsPage />} />
        <Route
          element={<ProtectedRoute subject="OrganizationNetworkIntelligence" />}
        >
          <Route
            path={APP_ROUTE_PATHS.networkIntelligence}
            element={<NetworkIntelligencePage />}
          />
        </Route>
        <Route element={<ProtectedRoute subject="OrganizationBudgets" />}>
          <Route
            path={APP_ROUTE_PATHS.budgets}
            element={<BudgetsController />}
          />
        </Route>
        <Route
          element={<ProtectedRoute subject="OrganizationCustomDashboards" />}
        >
          <Route
            path={APP_ROUTE_PATHS.customDashboards}
            element={<CustomDashboardsController />}
          />
        </Route>
        <Route path={APP_ROUTE_PATHS.apa} element={<ApaPage />} />
        <Route element={<ProtectedRoute subject="OrganizationNotifications" />}>
          <Route
            path={APP_ROUTE_PATHS.notifications}
            element={<NotificationsPage />}
          />
        </Route>
        {/* More specific than organization/management/* — must precede settings. */}
        <Route
          path={APP_ROUTE_PATHS.legacyAccessControlSso}
          element={
            <PathReplaceRedirect
              from={
                /^\/organization\/management\/access-control\/sso-connections/
              }
              to="/organization/management/sso-connections"
            />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.accessControl}
          element={<AccessControlController />}
        />
        <Route
          path={APP_ROUTE_PATHS.ssoConnections}
          element={<SsoController />}
        />
        <Route element={<ProtectedRoute subject="OrganizationSettings" />}>
          <Route
            path={APP_ROUTE_PATHS.settings}
            element={<SettingsController />}
          />
        </Route>
        <Route path={APP_ROUTE_PATHS.enterprise} element={<EnterprisePage />} />
        <Route path={APP_ROUTE_PATHS.apiAccess} element={<ApiAccessPage />} />
        <Route element={<ProtectedRoute subject="OrganizationAuditLog" />}>
          <Route path={APP_ROUTE_PATHS.eventLog} element={<EventLogPage />} />
        </Route>

        {/* ── Compat redirects (shared/console/legacy OneClick paths) ── */}
        <Route
          path={APP_ROUTE_PATHS.legacyExternalClusters}
          element={<PrependAutomationRedirect />}
        />
        <Route
          path={APP_ROUTE_PATHS.legacyNetworkIntelligence}
          element={<PrependAutomationRedirect />}
        />
        <Route
          path={APP_ROUTE_PATHS.legacyOrganizationCostReport}
          element={
            <PathReplaceRedirect
              from={/^\/organization\/cost-report/}
              to="/automation/cost-report"
            />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.legacyAccessControl}
          element={
            <PathReplaceRedirect
              from={/^\/access-control/}
              to="/organization/management/access-control"
            />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.legacyEventLog}
          element={
            <PathReplaceRedirect
              from={/^\/automation\/event-log/}
              to="/activity/events"
            />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.legacyAuditLog}
          element={
            <PathReplaceRedirect from={/^\/audit-log/} to="/activity/events" />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.legacyNotifications}
          element={
            <PathReplaceRedirect
              from={/^\/notifications/}
              to="/activity/notifications"
            />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.legacyBudgets}
          element={
            <PathReplaceRedirect
              from={/^\/automation\/budgets/}
              to="/automation/cost-report/budgets"
            />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.legacyClusterList}
          element={<Navigate to={BASE_PATHS.clusters} replace />}
        />
        <Route
          path={APP_ROUTE_PATHS.legacyAutomationSchedules}
          element={
            <PathReplaceRedirect
              from={/^\/automation\/schedules/}
              to="/organization/management"
            />
          }
        />
        <Route
          path={APP_ROUTE_PATHS.legacySettingsSchedules}
          element={
            <PathReplaceRedirect
              from={/^\/settings/}
              to="/organization/management"
            />
          }
        />

        <Route
          path="*"
          element={<Navigate to={BASE_PATHS.clusters} replace />}
        />
      </Route>

      <Route
        path={APP_ROUTE_PATHS.onboarding}
        element={
          <AppLayout noSidebar>
            <OnboardingPage />
          </AppLayout>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </GlobalSearchProvider>
);
